import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { GUEST_USER_ID } from '../lib/auth';
import { clearAllHubKeys, clearNonGuestHubKeys } from '../services/cache';

const GUEST_SESSION_KEY = 'hub:guest:session';

function buildGuestUser(): User {
  return {
    id: GUEST_USER_ID,
    email: 'guest@example.com',
    app_metadata: {},
    user_metadata: {},
    aud: 'authenticated',
    created_at: new Date().toISOString(),
  } as User;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string, location?: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  loginAsGuest: () => Promise<void>;
  /** True after a password-reset link signs the reader in, until they choose a new password. */
  passwordRecovery: boolean;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const prevUserIdRef = useRef<string | null>(null);
  // Set by an explicit action (e.g. loginAsGuest) that can race the initial
  // getSession() fetch below; guards against that fetch clobbering it once it resolves.
  const explicitAuthRef = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!explicitAuthRef.current) {
        let restoredGuest = false;
        if (!session) {
          let marker: string | null = null;
          try { marker = localStorage.getItem(GUEST_SESSION_KEY); } catch { /* ignore */ }
          if (marker === '1') {
            const guestUser = buildGuestUser();
            explicitAuthRef.current = true;
            prevUserIdRef.current = guestUser.id;
            setUser(guestUser);
            setSession({ user: guestUser } as Session);
            restoredGuest = true;
          }
        }
        if (!restoredGuest) {
          setSession(session);
          setUser(session?.user ?? null);
          prevUserIdRef.current = session?.user?.id ?? null;
        }
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        if (_event === 'INITIAL_SESSION' && explicitAuthRef.current) {
          setLoading(false);
          return;
        }
        if (_event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
        if ((_event === 'SIGNED_IN' || _event === 'PASSWORD_RECOVERY') && session?.user?.id && session.user.id !== prevUserIdRef.current) {
          clearAllHubKeys();
        }
        setSession(session);
        setUser(session?.user ?? null);
        prevUserIdRef.current = session?.user?.id ?? null;
        setLoading(false);
      })();
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string, location?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (!error && data.user) {
      await supabase.from('profiles').insert({
        id: data.user.id,
        email: email,
        full_name: fullName,
        location: location || null,
      });

      await supabase.from('user_preferences').insert({
        user_id: data.user.id,
      });

      await supabase.from('daily_briefing_preferences').insert({
        user_id: data.user.id,
      });

      // Note: No automatic subscription created - users must subscribe to a paid plan
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (!error) setPasswordRecovery(false);
    return { error };
  };

  const signOut = async () => {
    setPasswordRecovery(false);
    clearAllHubKeys();
    try {
      await supabase.auth.signOut();
    } finally {
      setUser(null);
      setSession(null);
    }
  };

  const loginAsGuest = async () => {
    explicitAuthRef.current = true;
    clearNonGuestHubKeys();
    try { localStorage.setItem(GUEST_SESSION_KEY, '1'); } catch { /* ignore */ }

    const guestUser = buildGuestUser();

    prevUserIdRef.current = guestUser.id;
    setUser(guestUser);
    setSession({ user: guestUser } as Session);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut, loginAsGuest, passwordRecovery, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
